import React from "react";
import { AxiosError } from "axios";
import { useQuery, QueryObserverResult, UseQueryOptions } from "react-query";

import { FetcherQueryKey, computeIsQueryEnabled } from "../../../services";
import { User } from "../../../models";

import { useMaybeUserId } from "../userId";

import { useSetUserGlobalActivityAndStatus } from "./user.globalStatus";
import {
  UserFetcherParams,
  userQueryKeyPrefix,
  computeUserQueryKey,
  userFetcher,
  onLoading,
  onSuccess,
  onError,
  isDataEqual,
} from "./user.query.helpers";

export { userQueryKeyPrefix, computeUserQueryKey };

// ##################
// ## useUserQuery ##
// ##################

export type ParamsOfUseUserQuery = {
  watch?: UseQueryOptions["notifyOnChangeProps"];
};
export type ResultOfUseUserQuery = QueryObserverResult<User, AxiosError>;
export type UseUserQuery = (
  params?: ParamsOfUseUserQuery,
) => ResultOfUseUserQuery;

/**
 * The returned value of this hook will contain the React Query result
 * of the fetching of the user data.
 *
 * This hook is only meant for use by the hooks in `./user.ts`.
 * Do not use this hook outside these hooks as this could couple us to React Query.
 */
export const useUserQuery: UseUserQuery = ({ watch } = {}) => {
  const userId = useMaybeUserId();

  const setUserGlobalActivityAndStatus = useSetUserGlobalActivityAndStatus();

  const params: UserFetcherParams = { userId };
  const resultOfUseQuery = useQuery<
    User,
    AxiosError,
    User,
    FetcherQueryKey<UserFetcherParams>
  >(computeUserQueryKey(params), userFetcher, {
    enabled: computeIsQueryEnabled(params),
    notifyOnChangeProps: watch || "tracked",
    onSuccess: () => onSuccess({ setUserGlobalActivityAndStatus }),
    onError: () => onError({ setUserGlobalActivityAndStatus }),
    isDataEqual,
  });

  const isStatusLoading = resultOfUseQuery.status === "loading";
  React.useEffect(() => {
    if (isStatusLoading) {
      onLoading({ setUserGlobalActivityAndStatus });
    }
  }, [isStatusLoading, setUserGlobalActivityAndStatus]);

  return resultOfUseQuery;
};
